import { useState, useCallback, useEffect } from 'react';
import { useUpdateUser, UpdateUserApiResponse } from '../../api/users';
import { useAppContext } from '../../contexts/Storage';

export interface ProfileFormData {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  imageUrl: string;
}

interface UseProfileOptions {
  onUpdateSuccess?: (data: UpdateUserApiResponse) => void;
  onUpdateError?: (error: Error) => void;
}

const useProfile = (options?: UseProfileOptions) => {
  const { user, token } = useAppContext();

  const [formData, setFormData] = useState<ProfileFormData>({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    imageUrl: '',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email || '',
        imageUrl: user.imageUrl || '',
      });
    }
  }, [user]);

  const updateUserMutation = useUpdateUser({
    onSuccess: (data) => {
      setIsEditing(false);
      setVerificationCode('');
      // Sync form with saved first/last name from API response
      if (data.user) {
        setFormData((prev) => ({
          ...prev,
          firstName: data.user.first_name ?? '',
          lastName: data.user.last_name ?? '',
          imageUrl: data.user.image_url ?? prev.imageUrl,
        }));
      }
      options?.onUpdateSuccess?.(data);
    },
    onError: (error) => {
      options?.onUpdateError?.(error);
    },
  });

  const updateFormField = useCallback((field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveProfile = useCallback(async () => {
    if (!token) {
      options?.onUpdateError?.(new Error('User not authenticated'));
      return;
    }

    const updateParams: {
      username?: string;
      firstname?: string;
      lastname?: string;
      email?: string;
      image_url?: string;
      code?: string;
    } = {};

    if (formData.username) {
      updateParams.username = formData.username;
    }
    updateParams.firstname = formData.firstName ?? '';
    updateParams.lastname = formData.lastName ?? '';
    updateParams.email = formData.email || user?.email || '';
    if (formData.imageUrl) {
      updateParams.image_url = formData.imageUrl;
    }
    if (verificationCode) {
      updateParams.code = verificationCode;
    }

    try {
      await updateUserMutation.mutateAsync(updateParams);
    } catch {
      // Error is already handled by the mutation's onError callback
    }
  }, [formData, verificationCode, token, user?.email, updateUserMutation, options]);

  const handleCancelEdit = useCallback(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email || '',
        imageUrl: user.imageUrl || '',
      });
    }
    setIsEditing(false);
    setVerificationCode('');
  }, [user]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  return {
    formData,
    isEditing,
    verificationCode,
    isUpdating: updateUserMutation.isPending,
    updateError: updateUserMutation.error,
    updateFormField,
    setVerificationCode,
    handleSaveProfile,
    handleCancelEdit,
    startEditing,
    setFormData,
  };
};

export default useProfile;
